import React, { useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useStores } from '../../../../hooks/use-stores';
import EditorToolbar from './EditorToolbar';
import BlockPalette from './BlockPalette';
import QuestionCanvas from './QuestionCanvas';
import PropertiesPanel from './PropertiesPanel';
import PreviewModal from './PreviewModal';
import {
    FlexDiv,
    Block,
    Heading3,
    ThemedButton,
    TextDefault,
} from '@awarego-ui/components';
import './VisualEditor.scss';

const BLOCK_TYPES = {
    // Interactive blocks
    radio: {
        name: 'Radio Buttons',
        icon: '🔘',
        category: 'interactive',
        defaultProps: {
            title: 'New Radio Question',
            options: ['Option 1', 'Option 2'],
            required: true,
        },
    },
    checkbox: {
        name: 'Checkboxes',
        icon: '☑️',
        category: 'interactive',
        defaultProps: {
            title: 'New Checkbox Question',
            options: ['Option 1', 'Option 2'],
            required: true,
        },
    },
    numericalscale: {
        name: 'Numerical Scale',
        icon: '📊',
        category: 'interactive',
        defaultProps: {
            title: 'New Scale Question',
            min: 1,
            max: 10,
            required: true,
        },
    },
    'yes-no-list': {
        name: 'Yes/No List',
        icon: '✅',
        category: 'interactive',
        defaultProps: {
            title: 'New Yes/No Question',
            questions: [
                { text: 'Question 1', required: true },
                { text: 'Question 2', required: true },
            ],
        },
    },
    orderablelist: {
        name: 'Orderable List',
        icon: '📋',
        category: 'interactive',
        defaultProps: {
            title: 'New Orderable List',
            items: ['Item 1', 'Item 2', 'Item 3'],
            required: true,
        },
    },
    conversation: {
        name: 'Conversation',
        icon: '💬',
        category: 'interactive',
        defaultProps: {
            title: 'New Conversation',
            messages: [
                { sender: 'User', text: 'Hello' },
                { sender: 'Bot', text: 'Hi there!' },
            ],
        },
    },
    'password-tester': {
        name: 'Password Tester',
        icon: '🔐',
        category: 'interactive',
        defaultProps: {
            title: 'Test Password Strength',
            required: true,
        },
    },
    decision: {
        name: 'Decision Tree',
        icon: '🌳',
        category: 'interactive',
        defaultProps: {
            title: 'New Decision',
            video: null,
            required: true,
        },
    },
    questionwithbuttons: {
        name: 'Question with Buttons',
        icon: '🔘',
        category: 'interactive',
        defaultProps: {
            title: 'New Button Question',
            buttons: ['Yes', 'No'],
            required: true,
        },
    },
    questionwithimage: {
        name: 'Question with Image',
        icon: '🖼️',
        category: 'interactive',
        defaultProps: {
            title: 'New Image Question',
            imageUrl: '',
            required: true,
        },
    },
    questionwithsocialpost: {
        name: 'Social Post Question',
        icon: '📱',
        category: 'interactive',
        defaultProps: {
            title: 'New Social Post Question',
            postData: {},
            required: true,
        },
    },
    groupOfBlocks: {
        name: 'Group of Blocks',
        icon: '📦',
        category: 'layout',
        defaultProps: {
            title: 'New Group',
            blocks: [],
        },
    },
};

const PRESENTATION_TYPES = {
    illustration: {
        name: 'Illustration',
        icon: '🎨',
        category: 'presentation',
        defaultProps: {
            title: 'New Illustration',
            imageUrl: '',
            hotspots: [],
        },
    },
    'interactive-img': {
        name: 'Interactive Image',
        icon: '🖱️',
        category: 'presentation',
        defaultProps: {
            title: 'New Interactive Image',
            imageUrl: '',
            clickableAreas: [],
        },
    },
    email: {
        name: 'Email Quiz',
        icon: '📧',
        category: 'presentation',
        defaultProps: {
            title: 'New Email Quiz',
            subject: 'Test Email',
            sender: 'test@example.com',
            content: 'Email content here',
        },
    },
    message: {
        name: 'Message',
        icon: '💬',
        category: 'presentation',
        defaultProps: {
            title: 'New Message',
            content: 'Message content here',
        },
    },
    password: {
        name: 'Password',
        icon: '🔑',
        category: 'presentation',
        defaultProps: {
            title: 'New Password',
            content: 'Password content here',
        },
    },
    question: {
        name: 'Question',
        icon: '❓',
        category: 'presentation',
        defaultProps: {
            title: 'New Question',
            content: 'Question content here',
        },
    },
    video: {
        name: 'Video',
        icon: '🎥',
        category: 'presentation',
        defaultProps: {
            title: 'New Video',
            videoUrl: '',
            autoplay: false,
        },
    },
    custom: {
        name: 'Custom Presentation',
        icon: '⚙️',
        category: 'presentation',
        defaultProps: {
            title: 'New Custom Presentation',
            content: 'Custom content here',
        },
    },
};

function VisualEditor({ initialQuestion = null, onSave, onCancel, companyId }) {
    const { authStore } = useStores();
    const { currentUser } = authStore;

    // Editor state
    const [question, setQuestion] = useState(() => {
        if (initialQuestion) {
            return {
                ...initialQuestion,
                ui: initialQuestion.ui || { blocks: [], presentation: null },
            };
        }
        return {
            id: null,
            name: 'New Question',
            description: '',
            area: 'General',
            difficulty: 'basic',
            behaviours: [],
            ui: {
                blocks: [],
                presentation: null,
                minimumBlockCount: 1,
            },
        };
    });

    const [selectedBlock, setSelectedBlock] = useState(null);
    const [selectedPresentation, setSelectedPresentation] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Update question and mark as dirty
    const updateQuestion = useCallback((updates) => {
        setQuestion((prev) => ({ ...prev, ...updates }));
        setIsDirty(true);
    }, []);

    // Add a new block
    const addBlock = useCallback(
        (blockType, position = -1) => {
            const blockConfig = BLOCK_TYPES[blockType];
            if (!blockConfig) return;

            const newBlock = {
                id: `block_${Date.now()}`,
                type: blockType,
                actionId: `action_${Date.now()}`,
                ...blockConfig.defaultProps,
            };

            const newBlocks = [...question.ui.blocks];
            if (position === -1) {
                newBlocks.push(newBlock);
            } else {
                newBlocks.splice(position, 0, newBlock);
            }

            updateQuestion({
                ui: {
                    ...question.ui,
                    blocks: newBlocks,
                },
            });

            setSelectedBlock(newBlock);
        },
        [question.ui, updateQuestion]
    );

    // Remove a block
    const removeBlock = useCallback(
        (blockId) => {
            const newBlocks = question.ui.blocks.filter(
                (block) => block.id !== blockId
            );
            updateQuestion({
                ui: {
                    ...question.ui,
                    blocks: newBlocks,
                },
            });

            if (selectedBlock?.id === blockId) {
                setSelectedBlock(null);
            }
        },
        [question.ui.blocks, selectedBlock, updateQuestion]
    );

    // Update a block
    const updateBlock = useCallback(
        (blockId, updates) => {
            const newBlocks = question.ui.blocks.map((block) =>
                block.id === blockId ? { ...block, ...updates } : block
            );

            updateQuestion({
                ui: {
                    ...question.ui,
                    blocks: newBlocks,
                },
            });

            if (selectedBlock?.id === blockId) {
                setSelectedBlock({ ...selectedBlock, ...updates });
            }
        },
        [question.ui.blocks, selectedBlock, updateQuestion]
    );

    // Move block
    const moveBlock = useCallback(
        (dragIndex, hoverIndex) => {
            const newBlocks = [...question.ui.blocks];
            const draggedBlock = newBlocks[dragIndex];
            newBlocks.splice(dragIndex, 1);
            newBlocks.splice(hoverIndex, 0, draggedBlock);

            updateQuestion({
                ui: {
                    ...question.ui,
                    blocks: newBlocks,
                },
            });
        },
        [question.ui.blocks, updateQuestion]
    );

    // Set presentation
    const setPresentation = useCallback(
        (presentationType) => {
            const presentationConfig = PRESENTATION_TYPES[presentationType];
            if (!presentationConfig) return;

            const newPresentation = {
                type: presentationType,
                ...presentationConfig.defaultProps,
            };

            updateQuestion({
                ui: {
                    ...question.ui,
                    presentation: newPresentation,
                },
            });

            setSelectedPresentation(newPresentation);
        },
        [question.ui, updateQuestion]
    );

    // Update presentation
    const updatePresentation = useCallback(
        (updates) => {
            updateQuestion({
                ui: {
                    ...question.ui,
                    presentation: {
                        ...question.ui.presentation,
                        ...updates,
                    },
                },
            });

            if (selectedPresentation) {
                setSelectedPresentation({
                    ...selectedPresentation,
                    ...updates,
                });
            }
        },
        [question.ui, updateQuestion]
    );

    // Save question
    const handleSave = useCallback(async () => {
        try {
            if (onSave) {
                await onSave(question);
            }
            setIsDirty(false);
        } catch (error) {
            console.error('Failed to save question:', error);
        }
    }, [question, onSave]);

    // Preview question
    const handlePreview = useCallback(() => {
        setShowPreview(true);
    }, []);

    // Create preview question for existing components
    const previewQuestion = useMemo(
        () => ({
            ...question,
            id: question.id || 'preview',
            definition: {
                ui: question.ui,
            },
        }),
        [question]
    );

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="visual-editor">
                <EditorToolbar
                    question={question}
                    isDirty={isDirty}
                    onSave={handleSave}
                    onPreview={handlePreview}
                    onCancel={onCancel}
                />

                <div className="visual-editor-content">
                    <div className="visual-editor-left">
                        <BlockPalette
                            blockTypes={BLOCK_TYPES}
                            presentationTypes={PRESENTATION_TYPES}
                            onAddBlock={addBlock}
                            onSetPresentation={setPresentation}
                        />
                    </div>

                    <div className="visual-editor-center">
                        <QuestionCanvas
                            question={question}
                            selectedBlock={selectedBlock}
                            selectedPresentation={selectedPresentation}
                            onSelectBlock={setSelectedBlock}
                            onSelectPresentation={setSelectedPresentation}
                            onUpdateBlock={updateBlock}
                            onUpdatePresentation={updatePresentation}
                            onRemoveBlock={removeBlock}
                            onMoveBlock={moveBlock}
                            onAddBlock={addBlock}
                        />
                    </div>

                    <div className="visual-editor-right">
                        <PropertiesPanel
                            selectedBlock={selectedBlock}
                            selectedPresentation={selectedPresentation}
                            question={question}
                            onUpdateBlock={updateBlock}
                            onUpdatePresentation={updatePresentation}
                            onUpdateQuestion={updateQuestion}
                        />
                    </div>
                </div>

                {showPreview && (
                    <PreviewModal
                        question={previewQuestion}
                        userName={currentUser?.name || 'Test User'}
                        userEmail={currentUser?.email || 'test@example.com'}
                        onClose={() => setShowPreview(false)}
                    />
                )}
            </div>
        </DndProvider>
    );
}

export default observer(VisualEditor);

